/**
 * Coast Guard forms that print one request, one person's day or one resource
 * rather than the whole incident, so each card opens with a picker.
 *
 * The picker's selection is exactly the payload the PDF exporter already
 * takes, so the card and the export always show the same sheet.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { activityLogSummaryQueryOptions } from 'components/IAP/ActivityLogs/hooks/useActivityLogSummary';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useResourcesInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import {
  ICS214_BOXES,
  ICS221_BOXES,
} from './documentBoxLabels';
import { Ics213RrInstanceList } from './Ics213RrInstanceList';
import { ics221CheckoffRows } from './documentDerivations';
import { IcsDocumentCard } from './IcsDocumentCard';

function EntityPicker({
  label,
  value,
  options,
  onChange,
  emptyMessage,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  onChange: (next: string) => void;
  emptyMessage: string;
}) {
  if (options.length === 0) {
    return (
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        {emptyMessage}
      </p>
    );
  }

  return (
    <Select value={value} onValueChange={(next) => onChange(next ?? '')}>
      <SelectTrigger size="sm" className="tw:w-96" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent className="dark">
        {options.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function Ics213RrDocumentCard() {
  return <Ics213RrInstanceList />;
}

export function Ics214DocumentCard() {
  const incidentId = useActualIncidentId();
  const { data: logs = [] } = useQuery(
    activityLogSummaryQueryOptions(incidentId)
  );
  const [logId, setLogId] = useState('');
  const options = logs.map((log) => ({
    id: log.id,
    label: `${log.logDate} — ${log.userName ?? 'Unnamed'}${
      log.positionName ? ` (${log.positionName})` : ''
    }`,
  }));
  const selectedId = logId || (options[0]?.id ?? '');
  const selected = logs.find((log) => log.id === selectedId);

  return (
    <div data-tour="resources-activity-log">
    <IcsDocumentCard
      title="Activity Log ICS-214"
      formname="ICS_214_CG"
      exportLabel="Preview ICS-214"
      boxes={ICS214_BOXES}
      inputData={
        selected
          ? {
              log: {
                userGuid: selected.userGuid,
                positionId: selected.positionId,
                positionName: selected.positionName,
                workGroupName: selected.workGroupName,
              },
              entries: selected.entries.map((entry) => ({
                activityDatetime: entry.activityDatetime,
                description: entry.description,
              })),
            }
          : undefined
      }
      enabled={Boolean(selected)}
      editedIn={{ label: 'the activity logs' }}
      toolbar={
        <EntityPicker
          label="Activity log"
          value={selectedId}
          options={options}
          onChange={setLogId}
          emptyMessage="No activity logs have been started on this incident yet."
        />
      }
      testIdPrefix="ics214-document"
    />
    </div>
  );
}

export function Ics221DocumentCard() {
  const { effectivePeriodNumber } = useICSContext();
  const { data: resources = [] } = useResourcesInIncident();
  const [resourceId, setResourceId] = useState('');
  const options = resources
    .filter((resource) => resource.checkout_datetime)
    .map((resource) => ({
      id: resource.resource_in_incident_id,
      label: resource.resource_name_identifier ?? resource.resource_in_incident_id,
    }));
  const selected = resourceId || (options[0]?.id ?? '');

  return (
    <div data-tour="demob-ics221">
      <IcsDocumentCard
        title="Demobilization Check-Out ICS-221"
        formname="ICS_221_CG"
        exportLabel="Preview ICS-221"
        boxes={ICS221_BOXES}
        derive={ics221CheckoffRows}
        inputData={selected ? { resourceId: selected } : undefined}
        enabled={Boolean(selected)}
        periodNumber={effectivePeriodNumber}
        editedIn={{ label: 'resource check-out' }}
        versionBaseId="ICS_221"
        formInstanceId={selected || undefined}
        toolbar={
          <EntityPicker
            label="Demobilizing resource"
            value={selected}
            options={options}
            onChange={setResourceId}
            emptyMessage="No resources have started check-out on this incident yet."
          />
        }
        testIdPrefix="ics221-document"
      />
    </div>
  );
}
